import { format } from 'date-fns';

import { useState } from 'react';
import { TaskEvent, TaskState } from '~/atoms/schemas/workflowSchemas';
import { TaskStatusBadge } from '~/components/ui/common/TaskStatusBadge';
import { EmailView } from './EmailView';
import { TaskEvents } from './TaskEvents';
import { TriggerConfirmation } from './TriggerConfirmation';

type TaskPanelProps = {
  taskName: string;
  taskState: TaskState;
  startedAt: string;
  isNew?: boolean;
};

type TradeDetail = {
  isin: string;
  price?: number | string;
  quantity?: number;
  action: string;
};

const TradeDetailsTable = ({ details }: { details: TradeDetail[] }) => (
  <table className="w-full border-collapse">
    <thead>
      <tr>
        <th
          className="py-2 px-3 text-left text-bluegray-400 border-bottom-1"
          style={{ borderColor: '#3a3a3a' }}
        >
          ISIN
        </th>
        <th
          className="py-2 px-3 text-left text-bluegray-400 border-bottom-1"
          style={{ borderColor: '#3a3a3a' }}
        >
          Price
        </th>
        <th
          className="py-2 px-3 text-left text-bluegray-400 border-bottom-1"
          style={{ borderColor: '#3a3a3a' }}
        >
          Quantity
        </th>
        <th
          className="py-2 px-3 text-left text-bluegray-400 border-bottom-1"
          style={{ borderColor: '#3a3a3a' }}
        >
          Action
        </th>
      </tr>
    </thead>
    <tbody>
      {details?.map((detail, index) => (
        <tr key={index}>
          <td
            className="py-2 px-3 border-bottom-1"
            style={{ borderColor: '#3a3a3a', backgroundColor: '#252525' }}
          >
            {detail.isin}
          </td>
          <td
            className="py-2 px-3 border-bottom-1"
            style={{ borderColor: '#3a3a3a', backgroundColor: '#252525' }}
          >
            {detail.price || 'N/A'}
          </td>
          <td
            className="py-2 px-3 border-bottom-1"
            style={{ borderColor: '#3a3a3a', backgroundColor: '#252525' }}
          >
            {detail.quantity ? detail.quantity.toLocaleString() : 'N/A'}
          </td>
          <td
            className="py-2 px-3 border-bottom-1"
            style={{ borderColor: '#3a3a3a', backgroundColor: '#252525' }}
          >
            <span
              className={`border-round px-2 py-1 ${detail.action === 'proceed' ? 'bg-green-800' : 'bg-red-800'}`}
            >
              {detail.action}
            </span>
          </td>
        </tr>
      ))}
    </tbody>
  </table>
);

const ActionTriggerBox = ({ children }: { children: React.ReactNode }) => {
  return (
    <div
      className="mt-2 p-2 border-1 border-round"
      style={{
        backgroundColor: '#1e1e1e',
        borderColor: '#3a3a3a',
      }}
    >
      <div className="font-medium flex align-items-center pb-2">
        <i className="pi pi-bell mr-2 text-sm" />
        <span className="text-sm">Action Trigger</span>
      </div>
      {children}
    </div>
  );
};

export const TaskPanel = ({
  taskName,
  taskState,
  startedAt,
  isNew,
}: TaskPanelProps) => {
  const [expanded, setExpanded] = useState(true);

  const getStatusColor = () => {
    if (taskState.status === 'completed') return '#4caf50';
    if (taskState.status === 'failed') return '#f44336';
    if (taskState.status === 'pending') return '#ff9800';
    return '#2196f3'; // running
  };

  const getIcon = () => {
    if (taskState.status === 'completed') return 'pi-check-circle';
    if (taskState.status === 'failed') return 'pi-times-circle';
    if (taskState.status === 'pending') return 'pi-clock';
    return 'pi-spin pi-spinner'; // running
  };

  const formattedTaskName = taskName
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .trim()
    .replace(/^./, (str) => str.toUpperCase());

  const events = Object.values(taskState.events || {}) as TaskEvent[];

  return (
    <div
      className="border-round mb-3"
      style={{
        backgroundColor: '#1e1e1e',
        transform: isNew ? 'translateY(5px)' : 'translateY(0)',
        transition: 'opacity 0.2s ease-in-out, transform 0.06s ease-in-out',
      }}
    >
      <div
        className="p-3 cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex justify-content-between align-items-center">
          <div className="flex align-items-center">
            <i
              className={`pi ${getIcon()} mr-2`}
              style={{ color: getStatusColor() }}
            />
            <span className="text-lg">{formattedTaskName}</span>
          </div>
          <div className="flex align-items-center">
            <TaskStatusBadge
              status={taskState.status || 'running'}
              size="x-small"
            />
            <i
              className={`pi ${expanded ? 'pi-chevron-up' : 'pi-chevron-down'} ml-2`}
            />
          </div>
        </div>
        <div className="text-sm text-color-secondary pt-1">
          <i className="pi pi-clock mr-1 text-xs pr-1" />
          {format(new Date(startedAt), 'MMM dd, yyyy HH:mm:ss')}
        </div>
      </div>

      {expanded && (
        <>
          <div className="p-2">
            {taskName === 'EmailCategorizerTask' ||
            taskName === 'ConfirmEmailTask' ||
            taskName === 'ComposeEmailTask' ||
            taskName === 'GetTradeEmailResponseTask' ||
            taskName === 'CompleteEmailTask' ||
            taskName === 'TradeCheckerTask' ? (
              <>
                {(taskState.task_input.type === 'EmailInput' ||
                  taskState.task_input.type === 'EnrichedEmailInput') && (
                  <EmailView
                    fromAddress={taskState.task_input.from_address}
                    toAddress={taskState.task_input.to_address}
                    subject={taskState.task_input.subject}
                    content={taskState.task_input.content}
                  />
                )}

                {taskName === 'ConfirmEmailTask' && taskState.checkpoint && (
                  <ActionTriggerBox>
                    <TriggerConfirmation
                      checkpoint={taskState.checkpoint}
                      isCompleted={taskState.status === 'completed'}
                    />
                  </ActionTriggerBox>
                )}

                {taskName === 'GetTradeEmailResponseTask' &&
                  taskState.checkpoint &&
                  taskState.checkpoint.type === 'EmailCheckpoint' &&
                  taskState.checkpoint.email_input && (
                    <ActionTriggerBox>
                      <EmailView
                        fromAddress={
                          taskState.checkpoint.email_input.from_address
                        }
                        toAddress={taskState.checkpoint.email_input.to_address}
                        subject={taskState.checkpoint.email_input.subject}
                        content={taskState.checkpoint.email_input.content}
                        maxHeight={200}
                      />
                    </ActionTriggerBox>
                  )}
              </>
            ) : taskName === 'ConfirmAndBookOrderTask' ? (
              <>
                {taskState.task_input.type ===
                  'TradeDetailsAgentContentResponse' && (
                  <div
                    className="border-round shadow-1 p-3"
                    style={{
                      backgroundColor: '#1e1e1e',
                      color: 'white',
                    }}
                  >
                    <div className="text-base mb-3 flex align-items-center">
                      <i
                        className="pi pi-money-bill mr-2"
                        style={{ color: '#5caedc' }}
                      />
                      <span>Trade Details</span>
                    </div>

                    <div className="mb-3">
                      <TradeDetailsTable
                        details={taskState.task_input.details || []}
                      />
                    </div>

                    <div className="mb-3">
                      <div className="font-medium text-bluegray-400 mb-1">
                        Explanation
                      </div>
                      <div
                        className="p-3 border-1 border-round"
                        style={{
                          backgroundColor: '#252525',
                          borderColor: '#3a3a3a',
                          whiteSpace: 'pre-wrap',
                          lineHeight: '1.5',
                        }}
                      >
                        {taskState.task_input.explanation}
                      </div>
                    </div>
                  </div>
                )}

                {taskState.checkpoint && (
                  <ActionTriggerBox>
                    <TriggerConfirmation
                      checkpoint={taskState.checkpoint}
                      confirmationActionText="Confirm this order?"
                      isCompleted={taskState.status === 'completed'}
                    />
                  </ActionTriggerBox>
                )}
              </>
            ) : taskName === 'TerminalTask' ? (
              <>
                {taskState.task_input.type ===
                  'TradeDetailsAgentContentResponse' && (
                  <div
                    className="border-round shadow-1 p-3"
                    style={{
                      backgroundColor: '#1e1e1e',
                      color: 'white',
                    }}
                  >
                    <div className="text-base mb-3 flex align-items-center">
                      <i
                        className="pi pi-check-circle mr-2"
                        style={{ color: '#4caf50' }}
                      />
                      <span>Trade Summary</span>
                    </div>

                    <div className="mb-3">
                      <TradeDetailsTable
                        details={taskState.task_input.details || []}
                      />
                    </div>

                    <div className="mb-3">
                      <div className="font-medium text-bluegray-400 mb-1">
                        Analysis Summary
                      </div>
                      <div
                        className="p-3 border-1 border-round"
                        style={{
                          backgroundColor: '#252525',
                          borderColor: '#3a3a3a',
                          whiteSpace: 'pre-wrap',
                          lineHeight: '1.5',
                        }}
                      >
                        {taskState.task_input.explanation}
                      </div>
                    </div>

                    <div className="mb-2">
                      <div className="font-medium text-bluegray-400 mb-1">
                        Status
                      </div>
                      <div
                        className="px-3 py-2 border-1 border-round flex align-items-center"
                        style={{
                          backgroundColor: '#252525',
                          borderColor: '#3a3a3a',
                        }}
                      >
                        <i
                          className="pi pi-check-circle mr-2"
                          style={{ color: '#4caf50' }}
                        />
                        <span>{taskState.output?.content || 'Completed'}</span>
                      </div>
                    </div>
                  </div>
                )}

                {taskState.checkpoint && (
                  <ActionTriggerBox>
                    <TriggerConfirmation
                      checkpoint={taskState.checkpoint}
                      isCompleted={taskState.status === 'completed'}
                    />
                  </ActionTriggerBox>
                )}
              </>
            ) : (
              <>
                <div className="mb-2">
                  <div className="font-medium mb-1 flex align-items-center">
                    <i
                      className="pi pi-upload mr-1"
                      style={{ color: '#5caedc' }}
                    />
                    <span>Input:</span>
                  </div>
                  <pre
                    className="p-2 border-round overflow-auto"
                    style={{
                      maxHeight: '150px',
                      backgroundColor: '#1e1e1e',
                      color: 'white',
                      border: 'none',
                      whiteSpace: 'pre-wrap',
                      fontSize: '0.9rem',
                    }}
                  >
                    {JSON.stringify(taskState.task_input, null, 2)}
                  </pre>
                </div>
                <div>
                  <div className="font-medium mb-1 flex align-items-center">
                    <i
                      className="pi pi-download mr-1"
                      style={{ color: '#5caedc' }}
                    />
                    <span>Output:</span>
                  </div>
                  <pre
                    className="p-2 border-round overflow-auto"
                    style={{
                      maxHeight: '150px',
                      backgroundColor: '#1e1e1e',
                      color: 'white',
                      border: 'none',
                      whiteSpace: 'pre-wrap',
                      fontSize: '0.9rem',
                    }}
                  >
                    {JSON.stringify(taskState.output, null, 2)}
                  </pre>
                </div>
              </>
            )}
          </div>

          {events.length > 0 && <TaskEvents events={events} />}
        </>
      )}
    </div>
  );
};
